import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ImageUrl, ScreenshotDataUrl, ScreenshotId } from '../../types/ids';
import {
  StateInfoItem,
  StateInfoItemRendererFactory,
} from '@webmate-web-core/shared/components/state/stateinfoitemrendererfactory';
import { CanvasComponent } from '@webmate-web-core/shared/components/canvas/canvas.component';
import {
  ImageDownloadRequest,
  ImageService,
} from '@webmate-web-core/shared/services/image.service';
import { faQuestionCircle } from '@fortawesome/pro-light-svg-icons';
import {
  ArrowOverlayData,
  ImageMetadata,
  ImageOverlay,
  RectangleOverlayData,
  TextOverlayData,
} from '@webmate-web-core/shared/api/rest/image.restapi';
import { SmallId } from '@webmate-web-core/shared/types/base-types';
import * as paper from 'paper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isScrollableImage } from '@webmate-web-core/shared/utils/image-scrollable';

export interface OverlayHitEvent {
  boundingBox: paper.Rectangle;
  paperEvent: paper.ToolEvent;
  overlayId?: SmallId;
  overlayHandleType?: string;
}

@UntilDestroy()
@Component({
  selector: 'wm-canvas-based-image',
  templateUrl: './canvas-based-image.component.html',
  styleUrls: ['./canvas-based-image.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CanvasBasedImageComponent
  implements OnChanges, AfterViewInit, AfterViewChecked, OnDestroy
{
  static readonly DefaultColor = '#dc0050';
  static readonly SemiTransparentColor = 'rgba(255, 255, 255, 0.2)';

  @Input() downloadable = false;

  @Input() imageId!: ScreenshotId;

  /**
   * If not provided, we derive it from imageId.
   * NOTE: this component intentionally assigns to this.imageUrl internally (same as your original),
   * because your template uses `imageUrl` to decide whether to render the canvas.
   */
  @Input() imageUrl?: ScreenshotDataUrl | ImageUrl;

  @Input() small = false;
  @Input() showOverlays = false;

  // TODO: get rid of stateinfoitems and use overlays instead
  @Input() infoItems: StateInfoItem[] = [];

  @Input() additionalOverlays?: ImageOverlay[];
  @Input() overlayHandlesFor?: SmallId[];

  @Input() scale = false; // currently unused (kept for API compatibility)
  @Input() fillContainer = false;
  @Input() editable = false;

  @Output() error = new EventEmitter<any>();
  @Output() overlayHit = new EventEmitter<OverlayHitEvent>();

  @ViewChild('canvas', { static: false }) canvas?: CanvasComponent;
  @ViewChild('canvascontainer', { static: false })
  canvasContainer?: ElementRef<HTMLDivElement>;

  protected readonly faQuestionCircle = faQuestionCircle;

  weHaveNoImage = false;
  isLoaded = false;
  metadata?: ImageMetadata;

  private htmlImage?: HTMLImageElement;
  private imageRaster?: paper.Raster;

  private imgWidth = 0;
  private imgHeight = 0;
  private imageBounds?: paper.Rectangle;

  // --- Race-condition guards ---
  private imageSeq = 0; // increments each time we start a new image load
  private metaSeq = 0; // increments each time we start a new metadata load

  // --- Paper/tool lifecycle ---
  private tool?: paper.Tool;
  private paperReadyForSeq = 0; // == imageSeq when paper+raster are initialized for current image

  // --- Resize observer ---
  private resizeObserver?: ResizeObserver;
  private observedContainer?: HTMLDivElement;

  // --- Redraw scheduling ---
  private redrawQueued = false;
  private redrawPending = false;

  constructor(
    private readonly imageService: ImageService,
    private readonly infoItemRendererFactory: StateInfoItemRendererFactory,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const imageIdChanged =
      'imageId' in changes &&
      changes.imageId?.previousValue !== changes.imageId?.currentValue;

    const imageUrlChanged =
      'imageUrl' in changes &&
      changes.imageUrl?.previousValue !== changes.imageUrl?.currentValue;

    const smallChanged =
      'small' in changes &&
      changes.small?.previousValue !== changes.small?.currentValue;

    const imageInputsChanged = imageIdChanged || imageUrlChanged || smallChanged;

    if (imageInputsChanged) {
      this.resolveEffectiveImageUrl();
      this.resetForNewImage();

      if (this.imageUrl) {
        this.startHtmlImageLoad(this.imageUrl.toString());
      } else {
        this.weHaveNoImage = true;
      }

      if (this.showOverlays && this.imageId) {
        this.fetchMetadataForCurrentImage();
      }
    }

    // If showOverlays turns on, fetch metadata (if we have an id).
    if ('showOverlays' in changes) {
      if (this.showOverlays && this.imageId) {
        this.fetchMetadataForCurrentImage();
      } else if (!this.showOverlays) {
        this.clearOverlayLayer();
      }
      this.requestRedraw();
    }

    // Any overlay-related input changes -> redraw (once ready).
    if (
      'additionalOverlays' in changes ||
      'overlayHandlesFor' in changes ||
      'editable' in changes
    ) {
      this.requestRedraw();
    }

    // StateInfo layer changes -> redraw.
    if ('infoItems' in changes) {
      this.requestRedraw();
    }

    // Fill container changes -> resize.
    if ('fillContainer' in changes) {
      this.resizeCanvas();
    }

    // If the view isn’t ready yet (because of @if), ngAfterViewChecked will retry init.
    this.tryInitPaperFromLoadedHtmlImage();
  }

  ngAfterViewInit() {
    this.attachResizeObserverIfPossible();

    // Subscribe once; respect `downloadable` dynamically.
    this.imageService.downloadRequest$
      .pipe(untilDestroyed(this))
      .subscribe((req) => {
        if (!this.downloadable) return;
        if (this.imageId && req?.imageId && this.imageId === req.imageId) {
          this.downloadImage(req);
        }
      });

    // If the HTML image already loaded before the canvas existed, initialize now.
    this.tryInitPaperFromLoadedHtmlImage();
    this.requestRedraw();
  }

  ngAfterViewChecked() {
    // Important for structural @if timing: canvas/container can appear after ngAfterViewInit.
    this.attachResizeObserverIfPossible();
    this.tryInitPaperFromLoadedHtmlImage();
  }

  ngOnDestroy() {
    // Invalidate any in-flight async callbacks
    this.imageSeq++;
    this.metaSeq++;

    try {
      this.resizeObserver?.disconnect();
    } catch {}

    try {
      this.tool?.remove();
    } catch {}

    try {
      this.imageRaster?.remove();
    } catch {}
  }

  refresh() {
    this.resizeCanvas();
  }

  refreshOverlays(metaData?: ImageMetadata) {
    // Treat refresh as authoritative: bump metaSeq so in-flight fetch won’t overwrite it.
    this.metaSeq++;

    if (metaData) {
      this.metadata = metaData;
      this.requestRedraw();
      return;
    }

    if (this.imageId) {
      this.fetchMetadataForCurrentImage();
    }
  }

  // --------------------------
  // Image + Paper initialization
  // --------------------------

  private resolveEffectiveImageUrl() {
    // If parent passes imageUrl, keep it. Otherwise derive from imageId.
    if (!this.imageUrl && this.imageId) {
      this.imageUrl = this.imageService.getImageUrl(
        this.imageId,
        this.small ? 'medium' : 'original'
      );
    }

    // Keep weHaveNoImage in sync (your original code only ever set it to true).
    this.weHaveNoImage = !this.imageUrl;
    this.cdRef.markForCheck();
  }

  private resetForNewImage() {
    // Invalidate pending image work
    this.imageSeq++;
    this.paperReadyForSeq = 0;

    this.isLoaded = false;
    this.htmlImage = undefined;

    this.imgWidth = 0;
    this.imgHeight = 0;
    this.imageBounds = undefined;

    // Remove old raster/tool if present
    try {
      this.tool?.remove();
    } catch {}
    this.tool = undefined;

    try {
      this.imageRaster?.remove();
    } catch {}
    this.imageRaster = undefined;

    // Clear layers if we have a canvas already
    this.clearAllKnownLayers();

    this.requestRedraw();
  }

  private startHtmlImageLoad(url: string) {
    const seq = ++this.imageSeq;

    const img = new Image();
    img.crossOrigin = 'anonymous'; // MUST be set before src
    img.onerror = () => {
      if (seq !== this.imageSeq) return;
      this.weHaveNoImage = true;
      this.isLoaded = false;
      this.error.emit();
      this.cdRef.markForCheck();
    };

    img.onload = () => {
      if (seq !== this.imageSeq) return;

      this.htmlImage = img;
      this.imgWidth = img.naturalWidth || img.width || 0;
      this.imgHeight = img.naturalHeight || img.height || 0;

      // If canvas already exists, initialize Paper now.
      this.tryInitPaperFromLoadedHtmlImage();
      this.requestRedraw();
      this.cdRef.markForCheck();
    };

    img.src = url;
  }

  private tryInitPaperFromLoadedHtmlImage() {
    if (!this.canvas) return;
    if (!this.htmlImage) return;
    if (this.imgWidth <= 0 || this.imgHeight <= 0) return;

    // Only init Paper once per current image sequence.
    if (this.paperReadyForSeq === this.imageSeq) return;

    const seq = this.imageSeq;
    const img = this.htmlImage;

    // Set logical canvas size (Paper operates in logical pixels)
    const canvasElem = this.canvas.theCanvas.nativeElement;
    canvasElem.width = this.imgWidth;
    canvasElem.height = this.imgHeight;

    const scope = this.canvas.activateAndGetPaperScope();
    scope.view.viewSize = new scope.Size(this.imgWidth, this.imgHeight);

    // Remove known layers and rebuild deterministically.
    this.clearAllKnownLayers();

    // Add image layer and raster
    this.canvas.addAndActivateLayer('image');
    const raster = new scope.Raster(img);
    this.imageRaster = raster;

    const onRasterReady = () => {
      // Ignore if we switched images while raster was loading.
      if (seq !== this.imageSeq) return;

      raster.bounds.topLeft = new scope.Point(0, 0);

      this.isLoaded = true;
      this.paperReadyForSeq = seq;

      this.updateImageBounds(scope);
      this.fitCanvasToImgAndContainer();
      this.installEventHandlers();

      this.requestRedraw();
      this.cdRef.markForCheck();
    };

    // If already loaded (cached/fast), don’t miss the event.
    if ((raster as any).loaded) {
      onRasterReady();
    } else {
      raster.onLoad = onRasterReady;
    }
  }

  private fetchMetadataForCurrentImage() {
    const id = this.imageId;
    if (!id) return;

    const seq = ++this.metaSeq;

    this.imageService
      .getImageMetadata(id)
      .pipe(untilDestroyed(this))
      .subscribe((metadata) => {
        // Ignore stale responses
        if (seq !== this.metaSeq) return;
        // Also ignore if imageId changed
        if (id !== this.imageId) return;

        this.metadata = metadata || undefined;
        this.requestRedraw();
        this.cdRef.markForCheck();
      });
  }

  // --------------------------
  // Resize / layout
  // --------------------------

  private attachResizeObserverIfPossible() {
    const el = this.canvasContainer?.nativeElement;
    if (!el) return;

    // If the container got recreated (structural @if), rebind observer.
    if (this.observedContainer === el && this.resizeObserver) return;

    this.resizeObserver?.disconnect();

    this.resizeObserver = new ResizeObserver(() => {
      this.resizeCanvas();
    });
    this.resizeObserver.observe(el);

    this.observedContainer = el;
  }

  resizeCanvas() {
    if (!this.canvas) return;
    if (!this.canvasContainer) return;
    if (this.imgWidth <= 0 || this.imgHeight <= 0) return;

    this.fitCanvasToImgAndContainer();
    this.cdRef.markForCheck();
  }

  fitCanvasToImgAndContainer() {
    if (!this.canvas) return;
    if (!this.canvasContainer) return;
    if (this.imgWidth <= 0 || this.imgHeight <= 0) return;

    const containerWidth = this.canvasContainer.nativeElement.clientWidth;
    const containerHeight = this.canvasContainer.nativeElement.clientHeight;
    if (containerWidth <= 0 || containerHeight <= 0) return;

    const canvas = this.canvas.theCanvas;

    if (!this.fillContainer && isScrollableImage(this.imgWidth, this.imgHeight)) {
      const scale = containerWidth / this.imgWidth;
      canvas.nativeElement.style.width = `${containerWidth}px`;
      canvas.nativeElement.style.height = `${this.imgHeight * scale}px`;
      return;
    }

    const scale = Math.min(
      containerWidth / this.imgWidth,
      containerHeight / this.imgHeight
    );

    canvas.nativeElement.style.width = `${this.imgWidth * scale}px`;
    canvas.nativeElement.style.height = `${this.imgHeight * scale}px`;
  }

  private updateImageBounds(scope: paper.PaperScope) {
    this.imageBounds = new scope.Rectangle(
      new scope.Point(0, 0),
      new scope.Size(this.imgWidth, this.imgHeight)
    );
  }

  // --------------------------
  // Drawing scheduling
  // --------------------------

  private requestRedraw() {
    this.redrawPending = true;
    if (this.redrawQueued) return;

    this.redrawQueued = true;
    requestAnimationFrame(() => {
      this.redrawQueued = false;

      if (!this.redrawPending) return;
      this.redrawPending = false;

      this.redrawIfReady();
    });
  }

  private redrawIfReady() {
    if (!this.canvas) return;

    // Only draw overlays/items once Paper is initialized for the current image
    if (this.paperReadyForSeq !== this.imageSeq) return;
    if (this.imgWidth <= 0 || this.imgHeight <= 0) return;

    this.drawStateInfoItems();

    if (this.showOverlays) {
      this.drawOverlays();
    } else {
      this.clearOverlayLayer();
    }

    const scope = this.canvas.getPaperScope();
    scope?.view?.update?.();
  }

  private clearAllKnownLayers() {
    if (!this.canvas) return;

    // Ignore errors if layers don’t exist (depends on CanvasComponent impl)
    try { this.canvas.removeLayer('image'); } catch {}
    try { this.canvas.removeLayer('overlays'); } catch {}
    try { this.canvas.removeLayer('activeissue'); } catch {}
    try { this.canvas.removeLayer('highlightedelements'); } catch {}
    try { this.canvas.removeLayer('text'); } catch {}
  }

  private clearOverlayLayer() {
    if (!this.canvas) return;
    try {
      this.canvas.removeLayer('overlays');
    } catch {}
  }

  private drawStateInfoItems() {
    if (!this.canvas) return;

    // stateinfoitems use absolute coordinates (not normalized)
    try { this.canvas.removeLayer('activeissue'); } catch {}
    try { this.canvas.removeLayer('highlightedelements'); } catch {}
    try { this.canvas.removeLayer('text'); } catch {}

    this.canvas.activateAndGetPaperScope();
    this.canvas.addAndActivateLayer('activeissue');

    if (this.infoItems) {
      this.infoItems.forEach((item) => {
        const renderer = this.infoItemRendererFactory.makeCanvasRenderer(item!.type);
        renderer.renderToCanvas(item!, this.canvas!);
      });
    }
  }

  // --------------------------
  // Overlays
  // --------------------------

    drawOverlays() {
      if (!this.canvas) return;
      if (this.imgWidth <= 0 || this.imgHeight <= 0) return;

      const scope = this.canvas.activateAndGetPaperScope();

      // --- Make overlays layer deterministic and EMPTY ---
      // Use CanvasComponent to activate overlays layer (keeps its internal bookkeeping),
      // then hard-clear the actual Paper layer contents.
      this.canvas.addAndActivateLayer('overlays');

      const overlayLayer = scope.project.activeLayer;
      overlayLayer.name = 'overlays';

      // IMPORTANT: remove any duplicate leaked 'overlays' layers (trail cause)
      // This is the key line that prevents stacked arrows during drag-redraw.
      scope.project.layers
        .filter(l => l !== overlayLayer && l.name === 'overlays')
        .forEach(l => l.remove());

      // IMPORTANT: clear all previous overlay items from THIS layer
      overlayLayer.removeChildren();

      // --- Collect overlays (your existing logic, but fixed by-id filtering) ---
      const overlays: ImageOverlay[] = [];

      const additional = this.additionalOverlays ?? [];
      const additionalById = new Map<SmallId, ImageOverlay>();
      for (const o of additional) {
        overlays.push(o);
        additionalById.set(o.id, o);
      }

      let serverOverlays = this.metadata?.overlays ?? [];

      // If editable and additionalOverlays represent the current truth, drop server overlays not present by id
      if (this.editable && additional.length > 0) {
        const allowedIds = new Set(additional.map(o => o.id));
        serverOverlays = serverOverlays.filter(o => allowedIds.has(o.id));
      }

      for (const o of serverOverlays) {
        if (!additionalById.has(o.id)) overlays.push(o);
      }

      // --- Draw ---
      for (const overlay of overlays) {
        const hidden = !!overlay.hidden;
        if (overlay.overlayData.type === 'RectangleOverlayData') {
          this.drawRectangle(overlay.overlayData, overlay.id, scope, hidden);
        } else if (overlay.overlayData.type === 'TextOverlayData') {
          this.drawText(overlay.overlayData, overlay.id, scope, hidden);
        } else if (overlay.overlayData.type === 'ArrowOverlayData') {
          this.drawCurvedArrow(overlay.id, scope, overlay.overlayData, hidden);
        }
      }

      scope.view.update();
    }

  drawRectangle(
    overlayData: RectangleOverlayData,
    overlayId: SmallId,
    scope: paper.PaperScope,
    hidden = false
  ) {
    const color = new paper.Color(
      overlayData.color || CanvasBasedImageComponent.DefaultColor
    );
    const whiteColor = new paper.Color('white');

    const r = overlayData.rectangle;
    const rectanglePath = new scope.Path.Rectangle(
      new scope.Rectangle(
        r.x * this.imgWidth,
        r.y * this.imgHeight,
        r.width * this.imgWidth,
        r.height * this.imgHeight
      )
    );

    rectanglePath.fillColor = new paper.Color('rgba(0, 0, 0, 0)');
    rectanglePath.strokeColor = color;
    rectanglePath.strokeWidth = 3;
    rectanglePath.dashArray = hidden ? [10, 10] : [];
    rectanglePath.data['overlayId'] = overlayId;

    if (this.overlayHandlesFor?.includes(overlayId)) {
      const handles: Array<[paper.Point, string]> = [
        [rectanglePath.bounds.topLeft, 'topLeft'],
        [rectanglePath.bounds.topRight, 'topRight'],
        [rectanglePath.bounds.bottomLeft, 'bottomLeft'],
        [rectanglePath.bounds.bottomRight, 'bottomRight'],
      ];

      for (const [pt, type] of handles) {
        const h = new scope.Path.Circle(pt, 4);
        h.fillColor = color;
        h.strokeColor = whiteColor;
        h.strokeWidth = 2;
        h.data['overlayId'] = overlayId;
        h.data['overlayHandleType'] = type;
      }
    }

    return rectanglePath;
  }

  drawText(
    data: TextOverlayData,
    overlayId: SmallId,
    scope: paper.PaperScope,
    hidden = false
  ) {
    const textItem = new scope.PointText(
      new scope.Point(data.position.x * this.imgWidth, data.position.y * this.imgHeight)
    );

    textItem.fillColor = new paper.Color(
      hidden
        ? CanvasBasedImageComponent.SemiTransparentColor
        : data.textColor || CanvasBasedImageComponent.DefaultColor
    );
    textItem.strokeColor = new paper.Color('white');
    textItem.strokeWidth = 1;
    textItem.content = data.text;
    textItem.fontWeight = data.fontWeight || '600';
    textItem.fontSize = data.fontSize || '32px';
    textItem.data['overlayId'] = overlayId;

    return textItem;
  }

  drawCurvedArrow(
    overlayId: SmallId,
    scope: paper.PaperScope,
    overlayData: ArrowOverlayData,
    hidden = false
  ) {
    const start = new scope.Point(
      overlayData.start.x * this.imgWidth,
      overlayData.start.y * this.imgHeight
    );
    const end = new scope.Point(
      overlayData.end.x * this.imgWidth,
      overlayData.end.y * this.imgHeight
    );

    const color = new paper.Color(
      overlayData.color || CanvasBasedImageComponent.DefaultColor
    );

    const maxHeadWidth = 15;
    const arrowWidthDelta = 10;
    const maxArrowHeadHeight = 30;

    const directionVector = end.subtract(start).normalize();
    const scalingFactor = Math.min(1, start.getDistance(end) / 100);

    const arrowHeadHeight = maxArrowHeadHeight * scalingFactor;
    const stemEnd = end.subtract(directionVector.multiply(arrowHeadHeight));

    const headWidth = maxHeadWidth * scalingFactor;
    const perpendicularVector = directionVector.rotate(90).multiply(headWidth / 2);

    const basePoint1 = stemEnd.add(perpendicularVector);
    const basePoint2 = stemEnd.subtract(perpendicularVector);

    const mainTriangle = new scope.Path({
      segments: [start, basePoint1, basePoint2],
      closed: true,
      fillColor: hidden ? CanvasBasedImageComponent.SemiTransparentColor : color,
      strokeColor: 'white',
      strokeWidth: 1,
      dashOffset: hidden ? 5 : 1,
    });

    const arrowheadPerpendicular = directionVector
      .rotate(90)
      .multiply((headWidth / 2) + arrowWidthDelta * scalingFactor);

    const arrowheadPoint1 = stemEnd.add(arrowheadPerpendicular);
    const arrowheadPoint2 = stemEnd.subtract(arrowheadPerpendicular);
    const arrowheadTip = stemEnd.add(directionVector.multiply(arrowHeadHeight));

    const arrowheadTriangle = new scope.Path({
      segments: [arrowheadTip, arrowheadPoint1, arrowheadPoint2],
      closed: true,
      fillColor: hidden ? CanvasBasedImageComponent.SemiTransparentColor : color,
      strokeColor: 'white',
      strokeWidth: 1,
      dashOffset: hidden ? 5 : 1,
    });

    if (this.overlayHandlesFor?.includes(overlayId)) {
      const startHandle = new scope.Path.Circle(start, 4);
      const endHandle = new scope.Path.Circle(end, 4);

      startHandle.fillColor = color;
      startHandle.strokeColor = new paper.Color('white');
      startHandle.strokeWidth = 2;
      startHandle.data['overlayId'] = overlayId;
      startHandle.data['overlayHandleType'] = 'start';

      endHandle.fillColor = color;
      endHandle.strokeColor = new paper.Color('white');
      endHandle.strokeWidth = 2;
      endHandle.data['overlayId'] = overlayId;
      endHandle.data['overlayHandleType'] = 'end';
    }

    const united = mainTriangle.unite(arrowheadTriangle);

      // Explicitly remove the source shapes to avoid any chance they remain in the scene graph
      mainTriangle.remove();
      arrowheadTriangle.remove();

      // Re-apply style to the united shape (some PaperJS versions copy style inconsistently)
      (united as any).fillColor = hidden ? CanvasBasedImageComponent.SemiTransparentColor : color;
      (united as any).strokeColor = 'white';
      (united as any).strokeWidth = 1;
      (united as any).dashOffset = hidden ? 5 : 1;

    united.data['overlayId'] = overlayId;

    return united;
  }

  // --------------------------
  // Mouse events / hit testing
  // --------------------------

  private modifyPaperEventForResizedCanvas(event: paper.ToolEvent) {
    if (!this.canvas) return;

    const canvasElem = this.canvas.theCanvas.nativeElement;
    const logicalWidth = canvasElem.width;
    const logicalHeight = canvasElem.height;

    const rect = canvasElem.getBoundingClientRect();
    const displayWidth = rect.width;
    const displayHeight = rect.height;
    if (displayWidth <= 0 || displayHeight <= 0) return;

    const scaleX = logicalWidth / displayWidth;
    const scaleY = logicalHeight / displayHeight;

    const dpr = window.devicePixelRatio || 1;

    const correctPoint = (point: paper.Point) =>
      new paper.Point((point.x * scaleX) / dpr, (point.y * scaleY) / dpr);

    // Paper event properties are mutable enough in practice for this use.
    event.point = correctPoint(event.point);
    event.lastPoint = correctPoint(event.lastPoint);
    event.delta = correctPoint(event.delta);
  }

  private installEventHandlers() {
    if (!this.canvas) return;
    if (!this.imageBounds) return;

    const scope = this.canvas.getPaperScope();
    if (!scope) return;

    // Remove prior tool to avoid stacking multiple handlers.
    try {
      this.tool?.remove();
    } catch {}
    this.tool = undefined;

    const tool = new scope.Tool();
    tool.activate();
    this.tool = tool;

    tool.onMouseMove = (event: paper.ToolEvent) => {
      this.modifyPaperEventForResizedCanvas(event);

      scope.project.activeLayer.selected = false;

      if (event.item) {
        if (this.editable) {
          event.item.selected = true;
        }

        const overlayId = event.item.data['overlayId'];
        if (overlayId) {
          this.overlayHit.emit({
            boundingBox: this.imageBounds!,
            paperEvent: event,
            overlayId,
            overlayHandleType: event.item.data['overlayHandleType'],
          });
        }
      }
    };

    let activeDragItemId: SmallId | undefined;
    let activeDragItemHandleType: string | undefined;

    tool.onMouseDown = (event: paper.ToolEvent) => {
      this.modifyPaperEventForResizedCanvas(event);

      if (event.item?.data['overlayId']) {
        activeDragItemId = event.item.data['overlayId'];
        activeDragItemHandleType = event.item.data['overlayHandleType'];
        this.overlayHit.emit({
          boundingBox: this.imageBounds!,
          paperEvent: event,
          overlayId: activeDragItemId,
          overlayHandleType: activeDragItemHandleType,
        });
      } else {
        activeDragItemId = undefined;
        activeDragItemHandleType = undefined;
        this.overlayHit.emit({
          boundingBox: this.imageBounds!,
          paperEvent: event,
          overlayId: undefined,
        });
      }
    };

    tool.onMouseDrag = (event: paper.ToolEvent) => {
      this.modifyPaperEventForResizedCanvas(event);

      if (!activeDragItemId && event.item?.data['overlayId']) {
        activeDragItemId = event.item.data['overlayId'];
        activeDragItemHandleType = event.item.data['overlayHandleType'];
      }

      this.overlayHit.emit({
        boundingBox: this.imageBounds!,
        paperEvent: event,
        overlayId: activeDragItemId,
        overlayHandleType: activeDragItemHandleType,
      });

      return false;
    };

    tool.onMouseUp = (event: paper.ToolEvent) => {
      this.modifyPaperEventForResizedCanvas(event);

      const overlayId = activeDragItemId ?? event.item?.data['overlayId'];
      const overlayHandleType =
        activeDragItemHandleType ?? event.item?.data['overlayHandleType'];

      activeDragItemId = undefined;
      activeDragItemHandleType = undefined;

      if (overlayId) {
        this.overlayHit.emit({
          boundingBox: this.imageBounds!,
          paperEvent: event,
          overlayId,
          overlayHandleType,
        });
      }
    };
  }

  private downloadImage(req: ImageDownloadRequest) {
    if (!this.canvas) return;

    try {
      const dataUrl = this.canvas.theCanvas.nativeElement.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.target = '_blank';
      a.download = req.name || 'screenshot';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      // If the canvas is tainted by CORS, toDataURL will throw.
      this.error.emit(e);
    }
  }
}
